/**
 * Public share page for a captioned video.
 *
 * Route: /c/:slug — accessible to anyone, no auth required. Designed as a
 * lightweight viral landing surface: full-bleed video + "Caption your own
 * video — free" CTA + MarkeeMix branding.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, Play, AlertTriangle, Loader2,
  Languages, Aperture, Globe,
} from "lucide-react";
import "./AutoCaptionsPage.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

const ASPECT_LABEL = { "16:9": "Landscape", "9:16": "Portrait / Reels", "1:1": "Square" };

export default function AutoCaptionsSharePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/auto-captions/share/${slug}`)
      .then((r) => setData(r.data))
      .catch((err) => setError(err?.response?.data?.detail || "Share link is invalid or expired."));
  }, [slug]);

  return (
    <div className="ac-share" data-testid="ac-share-page">
      {/* Top brand bar */}
      <header className="ac-share-bar">
        <button
          type="button"
          className="ac-share-brand"
          onClick={() => navigate("/")}
          data-testid="ac-share-brand"
        >
          <span className="ac-share-dot" />
          MarkeeMix
        </button>
        <button
          type="button"
          className="ac-btn ac-btn--accent"
          onClick={() => navigate("/tools/auto-captions")}
          data-testid="ac-share-cta-top"
        >
          <Sparkles size={14}/> Caption your video — free
        </button>
      </header>

      <main className="ac-share-main">
        {!data && !error && (
          <div style={{ textAlign: "center", padding: 80 }}>
            <Loader2 size={26} className="ac-spin" style={{ color: "var(--ac-purple)" }}/>
            <p style={{ marginTop: 14, color: "var(--ac-text-soft)" }}>Loading shared video…</p>
          </div>
        )}

        {error && (
          <div className="ac-share-error" data-testid="ac-share-error">
            <AlertTriangle size={36} style={{ color: "var(--ac-red)" }}/>
            <h2>This link no longer works</h2>
            <p>{error}</p>
            <button className="ac-btn" onClick={() => navigate("/tools/auto-captions")} data-testid="ac-share-cta-error">
              <Sparkles size={14}/> Caption your own video <ArrowRight size={14}/>
            </button>
          </div>
        )}

        {data && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="ac-share-content"
            data-aspect={data.aspect}
          >
            <div className="ac-share-video-wrap" data-aspect={data.aspect}>
              <video
                src={`${API}${data.video_url}`}
                controls
                playsInline
                autoPlay
                muted
                loop
                preload="auto"
                data-testid="ac-share-video"
              />
            </div>

            <aside className="ac-share-side">
              <p className="ac-share-eyebrow"><Play size={11}/> Captioned with MarkeeMix</p>
              <h1 className="ac-share-title">{data.title || "Captioned video"}</h1>
              <div className="ac-share-meta">
                <span><Aperture size={12}/> {ASPECT_LABEL[data.aspect] || data.aspect}</span>
                {data.translated_to && (
                  <span><Languages size={12}/> {data.translated_to.toUpperCase()}</span>
                )}
                {data.duration_sec ? (
                  <span><Globe size={12}/> {Math.round(data.duration_sec)}s</span>
                ) : null}
              </div>

              <div className="ac-share-cta-block">
                <h3>Make captions for your video — free</h3>
                <p>Upload a video, get burned-in animated captions in under a minute. No editing software.</p>
                <button
                  className="ac-btn ac-btn--accent"
                  onClick={() => navigate("/tools/auto-captions")}
                  data-testid="ac-share-cta-main"
                >
                  <Sparkles size={16}/> Caption your own video <ArrowRight size={16}/>
                </button>
              </div>

              <div className="ac-share-footer">
                <a href={`${API}${data.subtitle_url}`} target="_blank" rel="noreferrer" className="ac-link" data-testid="ac-share-srt">
                  Download SRT
                </a>
                <span>·</span>
                <a href="/" className="ac-link">About MarkeeMix</a>
              </div>
            </aside>
          </motion.div>
        )}
      </main>
    </div>
  );
}
